import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { SEOHead } from '@/components/feature/SEOHead';
import { SchemaOrg } from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qr8s6e3oq4jqumtpgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试');
      setFormStatus('error');
    }
  };

  const milestones = [
    { year: '2018', event: '含羞草研究院正式成立，以认知科学为起点开启知识探索之旅' },
    { year: '2019', event: '扩展至学习方法与效率提升领域，平台文章突破200篇' },
    { year: '2020', event: '推出批判性思维与哲学思辨专栏，拓展知识边界' },
    { year: '2021', event: '平台注册用户突破10万，形成活跃的学习社区' },
    { year: '2022', event: '推出"自我成长"系列内容，聚焦个人发展与心理韧性' },
    { year: '2023', event: '与中国科学院心理研究所建立合作，提升内容学术深度' },
    { year: '2024', event: '平台累计发布原创文章超过500篇，月度阅读量突破100万' },
    { year: '2025', event: '全面升级平台体验，推出个性化学习路径推荐系统' },
    { year: '2026', event: '持续深耕专业知识领域，致力于成为华语世界最具影响力的学习成长平台' },
  ];

  const values = [
    {
      icon: 'ri-lightbulb-flash-line',
      title: '求知求真',
      desc: '我们相信知识的力量，坚持用严谨的态度对待每一个研究课题，追求真理而非迎合。',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: '以人为本',
      desc: '关注每一位求知者的成长需求，设计符合认知规律的学习体验，让知识真正服务于人。',
    },
    {
      icon: 'ri-global-line',
      title: '开放共享',
      desc: '致力于打破知识壁垒，让优质学习资源触手可及，推动知识的民主化传播。',
    },
    {
      icon: 'ri-seedling-line',
      title: '持续进化',
      desc: '保持终身学习的心态，不断迭代内容和方法，与用户一同成长与进步。',
    },
  ];

  const orgSchema = {
    '@type': 'Organization',
    name: '含羞草研究院',
    url: 'https://www.mimosa-research.cn',
    description: '专注于知识探索、学习成长与自我提升的专业研究平台',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@mimosa-research.cn',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '海淀区',
      streetAddress: '中关村南大街5号',
     },
  };

  return (
    <Layout>
      <SEOHead
        title="关于我们 - 含羞草研究院"
        description="含羞草研究院成立于2018年，是一个专注于知识探索、学习成长与自我提升的专业平台。了解我们的品牌故事、核心价值观和联系方式。"
        keywords="含羞草研究院,关于含羞草研究院,含羞草研究院介绍,知识平台,学习成长社区"
        canonicalUrl="https://www.mimosa-research.cn/about"
      />
      <SchemaOrg schema={orgSchema} />

      <div className="bg-background-100 border-b border-background-200/60">
        <div className="container-page py-8 md:py-12">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mt-2">
            关于含羞草研究院
          </h1>
          <p className="text-foreground-600 text-sm md:text-base mt-3 max-w-2xl">
            像含羞草一样保持敏锐与谦逊，对世界永远充满好奇。我们致力于为每一位求知者点亮前行的灯。
          </p>
        </div>
      </div>

      <section className="py-12 md:py-18 bg-background-50">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                我们的故事
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  含羞草研究院成立于2018年，最初是由一群对认知科学充满热情的学者创建的线上知识分享小组。我们相信，在这个信息过载的时代，真正稀缺的不是信息，而是深度思考和系统化知识。
                </p>
                <p>
                  平台的名称灵感来源于含羞草——这种植物对外界刺激保持着敏锐的反应，正如我们希望每一位求知者都能保持对世界的好奇心和批判性思维。含羞草在受到触碰时会暂时闭合，但很快又会重新展开，这也象征着我们在面对知识挑战时的韧性——可以短暂迷茫，但永远不会停止探索。
                </p>
                <p>
                  从最初的认知科学专栏，到如今涵盖学习方法、自我成长、批判性思维、哲学思辨和效率提升六大核心领域，含羞草研究院已经发展成为华语世界备受认可的专业知识平台。
                </p>
              </div>
            </div>
            <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20scholarly%20team%20discussion%20scene%20in%20modern%20office%20with%20bookshelves%20and%20plants%2C%20natural%20light%2C%20diverse%20group%20of%20Asian%20professionals%2C%20inspiring%20atmosphere%2C%20editorial%20photography%20with%20soft%20beige%20tones&width=800&height=600&seq=about-story&orientation=landscape"
                alt="含羞草研究院团队"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-18 bg-background-100">
        <div className="container-page">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3">
              发展历程
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              从一个小小的知识分享社区到专业的学习成长平台
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {milestones.map((m, i) => (
              <div key={i} className="flex gap-4 md:gap-6 pb-6 md:pb-8 relative">
                {i < milestones.length - 1 && (
                  <div className="absolute left-[27px] top-12 bottom-0 w-px bg-background-300"></div>
                )}
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-heading text-sm font-semibold">{m.year}</span>
                </div>
                <div className="flex-1 pt-3.5">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-18 bg-background-50">
        <div className="container-page">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3">
              核心价值观
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              指引我们前行的四个核心理念
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((v, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-accent-100/80 rounded-full mx-auto mb-4">
                  <i className={`${v.icon} text-accent-600 text-xl md:text-2xl`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-18 bg-background-100">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                无论你是有合作意向、内容建议，还是想加入我们的作者团队，都欢迎随时与我们联系。
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-600">contact@mimosa-research.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">办公地址</p>
                    <p className="text-sm text-foreground-600">北京市海淀区中关村南大街5号</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">工作时间</p>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  发送消息
                </h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入您的消息（不超过500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-md font-medium text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 bg-primary-50 border border-primary-200 rounded-md text-sm text-primary-700">
                      消息已成功发送！我们会尽快与您联系。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                      {formError || '提交失败，请稍后重试。'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}